import { useTranslation } from "@redotech/redo-i18n";
import {
  getDropoffQrCodeByProvider,
  hasDropoffQrCodeUrlForProvider,
} from "@redotech/redo-return-core-common/dropoff/utils";
import {
  MerchantAppReturn,
  Return,
} from "@redotech/redo-return-core-common/return";
import { getReturner } from "@redotech/redo-return-core-common/returns/return-participants";
import {
  BLUE_YONDER_PROVIDER,
  type DropoffProviderType,
  dropoffProviderType,
  HAPPY_RETURNS_PROVIDER,
  RETURN_BEAR_PROVIDER,
  VIRTUAL_PROVIDER,
} from "@redotech/redo-return-dropoff-provider-common/dropoff-provider";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { getDatadogActionName } from "@redotech/redo-web/datadog-action-name";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { assertNever } from "@redotech/util/type";
import { memo, useCallback, useMemo, useState } from "react";
import { useResendLabel } from "../hooks/use-resend-label";

/**
 * Whether a dropoff provider stores its QR code in the new
 * `dropoffs.qrCodes` array (as opposed to a legacy provider-specific field).
 * Adding a new provider forces a decision via the exhaustive switch.
 */
function hasNewSchemaQrCodeUrl(
  return_: Return | MerchantAppReturn,
  provider: DropoffProviderType,
): boolean {
  switch (provider) {
    case BLUE_YONDER_PROVIDER:
    case VIRTUAL_PROVIDER:
    case HAPPY_RETURNS_PROVIDER:
      return hasDropoffQrCodeUrlForProvider(return_, provider);
    case RETURN_BEAR_PROVIDER:
      // ReturnBear does not store QR codes in `dropoffs.qrCodes`.
      return false;
    default:
      assertNever(provider);
  }
}

interface IResendModalProps {
  "data-dd-action-name"?: string;
  return_: Return | MerchantAppReturn;
  isOpen: boolean;
  onClose: () => void;
}

export const ResendModal = memo(function ResendModal(props: IResendModalProps) {
  return <ResendModalContent key={props.return_._id} {...props} />;
});

function ResendModalContent({
  return_,
  isOpen,
  onClose,
  "data-dd-action-name": datadogActionName,
}: IResendModalProps) {
  const { t } = useTranslation();
  const initialEmail = getReturner(return_).email || "";
  const [email, setEmail] = useState(initialEmail);

  const resendLabelMut = useResendLabel(return_);
  const errorMessage =
    resendLabelMut.isError && resendLabelMut.error instanceof Error
      ? resendLabelMut.error.message
      : null;

  const hasNormalShippingLabel = !!return_.shipment;
  // The dropoffs.qrCodes path iterates the provider enum and dispatches
  // through a switch so a new provider is enforced at compile time.
  const isQrCode = dropoffProviderType.some((provider) =>
    hasNewSchemaQrCodeUrl(return_, provider),
  );

  const blueYonderDropoffEntry = getDropoffQrCodeByProvider(
    return_,
    BLUE_YONDER_PROVIDER,
  );
  const isFedexEasyReturnsFailure =
    !!blueYonderDropoffEntry &&
    !blueYonderDropoffEntry.qrCodeUrl &&
    !hasNormalShippingLabel;

  const handleResendLabel = useCallback(() => {
    resendLabelMut.mutate({ emailOverride: email }, { onSuccess: onClose });
  }, [email, resendLabelMut, onClose]);

  const { title, subtitle, showWarning } = useMemo(() => {
    if (isFedexEasyReturnsFailure) {
      return {
        title: t("Send Shipping Label"),
        subtitle: t(
          "This Return was supposed to create a Fedex Easy Returns QR code, but failed to do so. Please review the activity timeline for how to continue as a Fedex Easy Return.",
        ),
        showWarning: true,
      };
    }

    if (isQrCode) {
      return {
        title: t("Resend QR code"),
        subtitle: t(
          "This will send an email with the QR code to the customer.",
        ),
        showWarning: false,
      };
    }

    return {
      title: t("Resend Shipping Label"),
      subtitle: t(
        "This will send an email with the shipping label(s) to the customer.",
      ),
      showWarning: false,
    };
  }, [isFedexEasyReturnsFailure, isQrCode, t]);
  const isDisabled = resendLabelMut.isPending || !email.trim();

  return (
    <RedoModal
      data-dd-action-name={getDatadogActionName(datadogActionName, "close")}
      isOpen={isOpen}
      modalSize="sm"
      onModalCloseRequested={onClose}
      primaryButton={{
        "data-dd-action-name": getDatadogActionName(datadogActionName, "send"),
        text: t("Send"),
        size: "sm",
        onClick: handleResendLabel,
        disabled: isDisabled,
        pending: resendLabelMut.isPending,
      }}
      secondaryButton={{
        "data-dd-action-name": getDatadogActionName(
          datadogActionName,
          "cancel",
        ),
        text: t("Cancel"),
        size: "sm",
        onClick: onClose,
        disabled: resendLabelMut.isPending,
      }}
      subtitle={subtitle}
      title={title}
    >
      <Flex dir="column" gap="xl">
        <RedoTextInput
          data-dd-action-name={getDatadogActionName(
            datadogActionName,
            "email_input",
          )}
          fullWidth
          label={t("Email address")}
          placeholder={t("Enter email address")}
          setValue={setEmail}
          size="sm"
          value={email}
        />

        {showWarning && (
          <Text fontSize="xs" textColor="error">
            {t(
              "Sending this will generate a new shipping label for the customer to package themselves.",
            )}
          </Text>
        )}

        {errorMessage && (
          <Text fontSize="xs" textColor="error">
            {errorMessage}
          </Text>
        )}
      </Flex>
    </RedoModal>
  );
}
